import type { ApPaymentRun } from '@dcc-erp/shared';
import { fmt, fmtDate, toNumber } from '@dcc-erp/shared';

import { useApPaymentRuns } from '../../lib/queries/ap';

// =============================================================================
// Payment-runs tab (B2) — lists cross-vendor payment runs from useApPaymentRuns.
// Each run is a DRAFT/PENDING/APPROVED/REJECTED header whose engine 4-eyes chain
// is driven by PaymentRunApprovalPanel; clicking a row opens that panel via the
// onOpenRun callback (wired to setRunPanel in ApPage), making the run lifecycle
// reachable in-app (submit/approve/reject/return for DRAFT/PENDING/RETURNED,
// read-only for terminal). Mirrors ApPaymentsTab structure + tbl-shell styling.
//
// WS9 — extracted verbatim from the ap.tsx route monolith. Presentational: the
// /ap route owns the row-click wiring via onOpenRun. Guarded by
// ap.payment-runs-tab.test.tsx (mounts <ApPage/>, clicks รอบจ่าย, opens a run).
// =============================================================================
export interface ApPaymentRunsTabProps {
  companyId: string | undefined;
  onOpenRun: (run: ApPaymentRun) => void;
}

// Accounting-status badge for a run — same badge classes as the bill rows.
function runStatusBadge(status: ApPaymentRun['status']) {
  if (status === 'APPROVED')
    return (
      <span className="badge positive">
        <span className="bdot" />
        อนุมัติแล้ว
      </span>
    );
  if (status === 'PENDING')
    return (
      <span className="badge warning">
        <span className="bdot" />
        รออนุมัติ
      </span>
    );
  if (status === 'REJECTED')
    return (
      <span className="badge negative">
        <span className="bdot" />
        ปฏิเสธ
      </span>
    );
  if (status === 'VOID')
    return (
      <span className="badge neutral">
        <span className="bdot" />
        ยกเลิก
      </span>
    );
  return (
    <span className="badge info">
      <span className="bdot" />
      ร่าง
    </span>
  );
}

export function ApPaymentRunsTab({ companyId, onOpenRun }: ApPaymentRunsTabProps) {
  const { data: runs = [] } = useApPaymentRuns(companyId);

  return (
    <div className="tbl-shell">
      <div className="filter-row" style={{ justifyContent: 'space-between' }}>
        <div className="muted" style={{ fontSize: 13 }}>
          รอบจ่ายข้ามผู้ขาย — คลิกแถวเพื่อเปิดสายอนุมัติ
        </div>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 480 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่รอบจ่าย</th>
              <th>วันที่จ่าย</th>
              <th>สถานะ</th>
              <th className="num">ยอดรวม</th>
              <th className="num">ภาษีหัก ณ ที่จ่าย</th>
              <th className="num">ยอดสุทธิ</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr>
                <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่พบรอบจ่าย
                </td>
              </tr>
            )}
            {runs.map((run) => (
              <tr
                key={run.id}
                style={{ cursor: 'pointer' }}
                data-testid={`run-row-${run.id}`}
                onClick={() => onOpenRun(run)}
              >
                <td className="code">{run.voucherNo}</td>
                <td>{fmtDate(run.paymentDate)}</td>
                <td>{runStatusBadge(run.status)}</td>
                <td className="num tnum">{fmt(toNumber(run.totalGross), 2)}</td>
                <td className="num tnum">{fmt(toNumber(run.totalWht), 2)}</td>
                <td className="num tnum">
                  <b>{fmt(toNumber(run.totalNet), 2)}</b>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
